/**
 * useRebookTargets — the DATA half of the rebook picker (BON-146): the month
 * state, the month-scoped `GET /workshops/:id/rebook-targets?bookingId=` fetch, and the
 * tree filter.
 *
 * Lives in the DRAWER, not the picker, because the drawer needs the envelope
 * before the picker ever opens: the cutoff verdict decides which notice it
 * shows, and `attendeeCount` decides the copy. The picker then renders whatever
 * rows this has for the visible month.
 *
 * Deliberately month-scoped, mirroring the create-booking picker: a booking's
 * same-price targets across a region and a whole season is a big list, and the
 * calendar can only show one month at a time anyway.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchRebookTargets, type RebookTargetsEnvelope } from '../../../api/rebooking';

import { fromRebookTarget, type PickerWorkshop } from './pickerWorkshop';
import { monthFetchRange } from './useCreateBookingWorkshops';
import { deriveTreeOptions } from './WorkshopPickerHelpers';

interface LoadedTargets {
  envelope: RebookTargetsEnvelope;
  workshops: PickerWorkshop[];
}

/** `bookingId` null lists targets for every confirmed booking on the workshop. */
export function useRebookTargets(bookingId: string | null, workshopId: string, month: Date) {
  const [data, setData] = useState<LoadedTargets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [treeSpeciesId, setTreeSpeciesId] = useState<string | null>(null);
  // One-shot: the filter seeds itself from the tree the customer originally
  // booked (decision 2), but only the FIRST time targets arrive. Re-seeding on
  // every month change would silently undo a CSR who cleared it to go looking.
  const [seeded, setSeeded] = useState(false);

  const { from, to } = useMemo(() => monthFetchRange(month), [month]);

  const load = useCallback(
    (signal: { aborted: boolean }) => {
      setLoading(true);
      setError(null);
      fetchRebookTargets(workshopId, { bookingId: bookingId ?? undefined, from, to })
        .then((envelope) => {
          if (signal.aborted) return;
          setData({ envelope, workshops: envelope.rows.map(fromRebookTarget) });
        })
        .catch((err: unknown) => {
          if (signal.aborted) return;
          setError(err instanceof Error ? err.message : 'Could not load workshops to move to.');
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [bookingId, workshopId, from, to],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Memoized rather than `data?.workshops ?? []`: a fresh literal every render
  // would re-run every downstream useMemo, and the picker maps this whole list
  // to calendar events.
  const workshops = useMemo(() => data?.workshops ?? [], [data]);
  const envelope = data?.envelope ?? null;

  // The tree the booking is already on. Resolved from the rows rather than
  // taken as a species id off the envelope: the envelope names the source
  // workshop TYPE, and the filter's options are keyed by SPECIES, so the two
  // are joined through a row that carries both. Null when this month happens to
  // contain no workshop of that type, which just means no default.
  const sourceSpeciesId = useMemo(() => {
    if (!envelope) return null;
    const row = envelope.rows.find((r) => r.workshopTypeId === envelope.sourceWorkshopTypeId);
    return row?.treeSpecies?.id ?? null;
  }, [envelope]);

  useEffect(() => {
    if (seeded || !envelope) return;
    setSeeded(true);
    if (sourceSpeciesId) setTreeSpeciesId(sourceSpeciesId);
  }, [seeded, envelope, sourceSpeciesId]);

  const treeOptions = useMemo(() => deriveTreeOptions(workshops), [workshops]);
  const filtered = useMemo(
    () => (treeSpeciesId ? workshops.filter((w) => w.treeSpeciesId === treeSpeciesId) : workshops),
    [workshops, treeSpeciesId],
  );

  return {
    envelope,
    workshops: filtered,
    treeOptions,
    treeSpeciesId,
    setTreeSpeciesId,
    loading,
    error,
  };
}
